// Visual Flows Directory Page (PRD §1.9, WP-08).
// List, filter, search, create, import, duplicate, export, delete, and migrate flows.
import { useState, useEffect, useMemo, useCallback, useId, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { useDialogA11y } from '../lib/useDialogA11y';
import { useAccounts, ALL_ACCOUNTS } from '../lib/accounts';
import { useToast } from '../components/ui/Toast';
import {
  flowList,
  flowDelete,
  flowDuplicate,
  migrateAutomationsToFlows,
  exportFlowJson,
  type FlowDoc,
  type FlowType,
} from '../lib/flows';
import { flowFolderList, type FlowFolderDoc } from '../lib/bots';
import { numberLabel } from '../lib/whatsappApi';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import {
  PlusIcon,
  UploadIcon,
  DownloadIcon,
  TrashIcon,
  CopyIcon,
  EditIcon,
  SearchIcon,
  RefreshIcon,
  SparklesIcon,
} from '../components/icons';
import ImportFlowModal from '../components/flow/ImportFlowModal';

export default function Flows() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedId, numbers } = useAccounts();

  // Data state
  const [flows, setFlows] = useState<FlowDoc[]>([]);
  const [folders, setFolders] = useState<FlowFolderDoc[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedFolder, setSelectedFolder] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals & confirmation state
  const [importOpen, setImportOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [migrateConfirmOpen, setMigrateConfirmOpen] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  // Dialog a11y (Escape / focus trap) for the inline confirm dialogs
  const deleteDialogRef = useRef<HTMLDivElement>(null);
  const deleteTitleId = useId();
  const closeDeleteDialog = useCallback(() => setDeleteConfirmId(null), []);
  useDialogA11y(deleteConfirmId !== null, deleteDialogRef, closeDeleteDialog);

  const migrateDialogRef = useRef<HTMLDivElement>(null);
  const migrateTitleId = useId();
  const closeMigrateDialog = useCallback(() => setMigrateConfirmOpen(false), []);
  useDialogA11y(migrateConfirmOpen, migrateDialogRef, closeMigrateDialog);

  // Fetch flows & folders
  const fetchFlows = useCallback(async () => {
    setLoading(true);
    try {
      const integrationParam =
        selectedId && selectedId !== ALL_ACCOUNTS ? selectedId : undefined;

      const [flowsRes, foldersRes] = await Promise.all([
        flowList({
          integrationId: integrationParam,
          type: selectedType !== 'all' ? (selectedType as FlowType) : undefined,
          folderId: selectedFolder !== 'all' ? selectedFolder : undefined,
        }),
        flowFolderList({}),
      ]);

      if (flowsRes.data.ok) {
        setFlows(flowsRes.data.flows || []);
      }
      if (foldersRes.data.ok) {
        setFolders(foldersRes.data.folders || []);
      }
    } catch (err: any) {
      toast.error(err.message || t('flow.list.fetchFailed'));
    } finally {
      setLoading(false);
    }
    // `toast` deliberately excluded: an error toast must not re-run the loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedType, selectedFolder, t]);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  // Client-side search filtering
  const filteredFlows = useMemo(() => {
    return flows.filter((f) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const matchName = f.name.toLowerCase().includes(q);
      const matchKeywords = (f.trigger?.keywords || []).some((k) => k.toLowerCase().includes(q));
      const matchSystem = f.trigger?.systemActionKey?.toLowerCase().includes(q);
      return matchName || matchKeywords || matchSystem;
    });
  }, [flows, searchQuery]);

  // Duplicate Flow
  const handleDuplicate = async (flow: FlowDoc) => {
    setDuplicatingId(flow.id);
    try {
      const res = await flowDuplicate({ id: flow.id });
      if (res.data.ok && res.data.flow) {
        toast.success(t('flow.duplicate.success'));
        fetchFlows();
      } else {
        toast.error('Failed to duplicate flow');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to duplicate flow');
    } finally {
      setDuplicatingId(null);
    }
  };

  // Delete Flow
  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      const res = await flowDelete({ id });
      if (res.data.ok) {
        toast.success(t('flow.delete.success'));
        setDeleteConfirmId(null);
        setFlows((prev) => prev.filter((f) => f.id !== id));
      } else {
        toast.error('Failed to delete flow');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete flow');
    } finally {
      setDeleting(false);
    }
  };

  // Export Flow JSON
  const handleExport = (flow: FlowDoc) => {
    exportFlowJson({
      name: flow.name,
      type: flow.type,
      integrationId: flow.integrationId,
      folderId: flow.folderId,
      trigger: flow.trigger,
      automationActions: flow.automationActions,
      dataIntegrations: flow.dataIntegrations,
      nodes: flow.nodes || [],
      edges: flow.edges || [],
    });
    toast.success(t('flow.export.success'));
  };

  // Migrate legacy automations to visual flows
  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const res = await migrateAutomationsToFlows({});
      if (res.data.ok) {
        toast.success(
          t('flow.migrate.success', { count: String(res.data.migratedCount || 0) }) ||
            `Migrated ${res.data.migratedCount || 0} automations to flows`
        );
        setMigrateConfirmOpen(false);
        fetchFlows();
      } else {
        toast.error('Migration failed');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to migrate automations');
    } finally {
      setMigrating(false);
    }
  };

  const getTypeName = (type: FlowType) => {
    switch (type) {
      case 'keyword':
        return t('flow.type.keyword');
      case 'system':
        return t('flow.type.system');
      case 'sequence':
        return t('flow.type.sequence');
      default:
        return type;
    }
  };

  const typePillClass = (type: FlowType) => {
    switch (type) {
      case 'keyword':
        return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'system':
        return 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'sequence':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      default:
        return 'bg-slate-100 text-ink-2 dark:bg-slate-800 dark:text-slate-300';
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink dark:text-white">
            {t('flow.list.title')}
          </h1>
          <p className="mt-1 text-sm text-ink-3 dark:text-slate-400">
            {t('flow.list.subtitle')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setMigrateConfirmOpen(true)}
            className="flex items-center gap-1.5 text-xs font-semibold"
            title={t('flow.migrate.buttonTooltip')}
          >
            <SparklesIcon className="h-4 w-4 text-brand" />
            <span>{t('flow.migrate.button')}</span>
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-1.5 text-xs font-semibold"
          >
            <UploadIcon className="h-4 w-4 text-ink-3 dark:text-slate-400" />
            <span>{t('flow.list.importFlow')}</span>
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate('/flows/new')}
            className="flex items-center gap-1.5 text-xs font-semibold shadow-sm"
          >
            <PlusIcon className="h-4 w-4" />
            <span>{t('flow.list.createFlow')}</span>
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-surface p-3.5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-center gap-3 flex-1 min-w-[280px]">
          {/* Search box */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <SearchIcon className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-3 dark:text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('flow.list.searchPlaceholder')}
              className="w-full rounded-xl border border-line bg-white ps-9 pe-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
            />
          </div>

          {/* Flow Type Filter */}
          <div className="flex items-center gap-1">
            {['all', 'keyword', 'system', 'sequence'].map((typeKey) => (
              <button
                key={typeKey}
                type="button"
                onClick={() => setSelectedType(typeKey)}
                className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                  selectedType === typeKey
                    ? 'bg-brand text-white shadow-xs'
                    : 'text-ink-2 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                {t(`flow.filter.${typeKey}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Folder Selector & Refresh */}
        <div className="flex items-center gap-2">
          {folders.length > 0 && (
            <select
              value={selectedFolder}
              onChange={(e) => setSelectedFolder(e.target.value)}
              className="rounded-xl border border-line bg-white px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
            >
              <option value="all">{t('flow.filter.allFolders')}</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            onClick={fetchFlows}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-surface text-ink-3 hover:bg-slate-100 hover:text-ink dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
            title={t('common.refresh')}
          >
            <RefreshIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col justify-between rounded-2xl border border-line bg-surface p-5 shadow-xs dark:border-slate-800 dark:bg-slate-900 space-y-4"
            >
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-20 rounded-md" />
                <Skeleton className="h-5 w-14 rounded-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <div className="flex items-center justify-between border-t border-line/60 pt-3 dark:border-slate-800">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-7 w-20 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredFlows.length === 0 ? (
        /* Empty State */
        <div className="rounded-2xl border border-dashed border-line bg-surface/40 p-12 text-center dark:border-slate-800 dark:bg-slate-900/40">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10 text-brand">
            <SparklesIcon className="h-7 w-7" />
          </div>
          <h3 className="mt-4 text-base font-bold text-ink dark:text-white">
            {searchQuery || selectedType !== 'all' || selectedFolder !== 'all'
              ? t('flow.list.emptyFiltered')
              : t('flow.list.emptyTitle')}
          </h3>
          <p className="mt-1 text-xs text-ink-3 dark:text-slate-400 max-w-sm mx-auto">
            {t('flow.list.emptySubtitle')}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/flows/new')}
              className="flex items-center gap-1.5 text-xs font-semibold"
            >
              <PlusIcon className="h-4 w-4" />
              <span>{t('flow.list.createFlow')}</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setImportOpen(true)}
              className="flex items-center gap-1.5 text-xs font-semibold"
            >
              <UploadIcon className="h-4 w-4" />
              <span>{t('flow.list.importFlow')}</span>
            </Button>
          </div>
        </div>
      ) : (
        /* Flow Cards Grid */
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredFlows.map((flow) => {
            const num = numbers.find((n) => n.id === flow.integrationId);
            const folder = folders.find((f) => f.id === flow.folderId);
            const nodeCount = flow.nodes?.length || 0;

            return (
              <div
                key={flow.id}
                className="group relative flex flex-col justify-between rounded-2xl border border-line bg-surface p-5 shadow-xs transition-all hover:border-brand/40 hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
              >
                <div>
                  {/* Top row: Type badge & Status */}
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${typePillClass(
                        flow.type
                      )}`}
                    >
                      {getTypeName(flow.type)}
                    </span>

                    {flow.published ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                        v{flow.version} • {t('flow.header.published')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-ink-3 dark:bg-slate-800 dark:text-slate-400">
                        v{flow.version} • {t('flow.header.draft')}
                      </span>
                    )}
                  </div>

                  {/* Flow Name & Trigger Preview */}
                  <div className="mt-3">
                    <h3
                      onClick={() => navigate(`/flows/${flow.id}`)}
                      className="cursor-pointer text-base font-bold text-ink hover:text-brand dark:text-white dark:hover:text-brand truncate"
                    >
                      {flow.name}
                    </h3>

                    {/* Trigger details */}
                    <div className="mt-2 text-xs text-ink-3 dark:text-slate-400">
                      {flow.type === 'keyword' && flow.trigger?.keywords?.length ? (
                        <p className="truncate">
                          <span className="font-semibold text-ink-2 dark:text-slate-300">
                            {t('flow.list.keywords')}:
                          </span>{' '}
                          {flow.trigger.keywords.slice(0, 3).join(', ')}
                          {flow.trigger.keywords.length > 3 ? ` +${flow.trigger.keywords.length - 3}` : ''}
                        </p>
                      ) : flow.type === 'system' && flow.trigger?.systemActionKey ? (
                        <p className="truncate">
                          <span className="font-semibold text-ink-2 dark:text-slate-300">
                            {t('flow.list.systemAction')}:
                          </span>{' '}
                          <code className="font-mono text-purple-600 dark:text-purple-400">
                            {flow.trigger.systemActionKey}
                          </code>
                        </p>
                      ) : (
                        <p className="text-ink-3 dark:text-slate-500 italic">
                          {t('flow.list.noTriggerSet')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bottom Meta & Action buttons */}
                <div className="mt-5 border-t border-line/70 pt-3 dark:border-slate-800">
                  <div className="flex items-center justify-between text-[11px] text-ink-3 dark:text-slate-400 mb-3">
                    <span>
                      {nodeCount} {nodeCount === 1 ? t('flow.list.node') : t('flow.list.nodes')}
                    </span>
                    <span>
                      {num ? numberLabel(num) : flow.integrationId ? t('flow.list.specificNumber') : t('flow.list.allNumbers')}
                    </span>
                    {folder && (
                      <span
                        className="inline-flex items-center rounded-md px-1.5 py-0.5 font-medium bg-info/20 text-info"
                        style={folder.color ? { backgroundColor: `${folder.color}20`, color: folder.color } : undefined}
                      >
                        {folder.name}
                      </span>
                    )}
                  </div>

                  {/* Action Toolbar */}
                  <div className="flex items-center justify-between gap-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/flows/${flow.id}`)}
                      className="flex-1 text-xs font-semibold"
                    >
                      <EditIcon className="h-3.5 w-3.5 me-1" />
                      <span>{t('flow.list.openBuilder')}</span>
                    </Button>

                    <button
                      type="button"
                      onClick={() => handleDuplicate(flow)}
                      disabled={duplicatingId === flow.id}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-surface text-ink-2 hover:bg-slate-100 hover:text-ink dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                      title={t('flow.list.duplicate')}
                    >
                      <CopyIcon className="h-4 w-4" />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleExport(flow)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-surface text-ink-2 hover:bg-slate-100 hover:text-ink dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                      title={t('flow.header.exportJson')}
                    >
                      <DownloadIcon className="h-4 w-4" />
                    </button>

                    <button
                      type="button"
                      onClick={() => setDeleteConfirmId(flow.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-200 bg-rose-50/50 text-rose-600 hover:bg-rose-100 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-400"
                      title={t('flow.list.delete')}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Import Modal */}
      <ImportFlowModal
        open={importOpen}
        onImportSuccess={() => fetchFlows()}
        onClose={() => setImportOpen(false)}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div
            ref={deleteDialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={deleteTitleId}
            className="w-full max-w-md rounded-2xl border border-line bg-surface p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900"
          >
            <h3 id={deleteTitleId} className="text-base font-bold text-ink dark:text-white">
              {t('flow.delete.confirmTitle')}
            </h3>
            <p className="mt-2 text-xs text-ink-2 dark:text-slate-300">
              {t('flow.delete.confirmMessage')}
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteConfirmId(null)}
                disabled={deleting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleting}
              >
                {deleting ? t('common.deleting') : t('common.delete')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Migrate Automations Confirmation Dialog */}
      {migrateConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div
            ref={migrateDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={migrateTitleId}
            className="w-full max-w-md rounded-2xl border border-line bg-surface p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand mb-3">
              <SparklesIcon className="h-6 w-6" />
            </div>
            <h3 id={migrateTitleId} className="text-base font-bold text-ink dark:text-white">
              {t('flow.migrate.confirmTitle')}
            </h3>
            <p className="mt-2 text-xs text-ink-2 dark:text-slate-300 leading-relaxed">
              {t('flow.migrate.confirmMessage')}
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMigrateConfirmOpen(false)}
                disabled={migrating}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleMigrate}
                disabled={migrating}
              >
                {migrating ? t('flow.migrate.inProgress') : t('flow.migrate.confirmAction')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
